import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  LayoutDashboard,
  Home,
  MonitorPlay,
  LogOut,
  Users,
  Inbox,
  UserRound,
  AlertCircle,
  FileText,
  BarChart3,
  Plus,
  Mic,
  Info,
  PenLine,
  Loader2,
  Download,
  ImageOff,
  Eye,
  EyeOff,
  UsersRound,
  Copy,
  Check,
  ChevronDown,
  ChevronUp,
  ShieldCheck,
  Building2,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { XiaBar } from '../components/ui/XiaBar';
import { IndicadorConexion } from '../components/IndicadorConexion';
import { QrSala } from '../components/QrSala';
import { PanelValidaciones } from '../components/PanelValidaciones';
import { BotonTema } from '../components/ui/BotonTema';
import { EsqueletoAportes, EsqueletoLineas } from '../components/ui/Esqueleto';
import { useAuthStore } from '../store/authStore';
import { useSurfaceTheme } from '../hooks/useSurfaceTheme';
import { useSesionActiva } from '../hooks/useSesionActiva';
import { useSalaViva, useColaPendientes } from '../hooks/useSalaViva';
import { ColaAudio } from '../components/ColaAudio';
import { useTiempoServidor } from '../components/Temporizador';
import { ApiError } from '../lib/api';
import {
  sesionApi,
  sesionKeys,
  elegirActividadFoco,
  resumirAporte,
  esMedia,
  type Aporte,
  type ActividadEstado,
  type MediaPayload,
} from '../lib/sesion';
import { useMediaUrl } from '../lib/media';
import { SelectorJornada } from '../components/cabina/SelectorJornada';
import { Itinerario } from '../components/cabina/Itinerario';
import { AccionPrincipal } from '../components/cabina/AccionPrincipal';
import { PanelConduccion } from '../components/cabina/paneles';

/**
 * Cabina (/cabina) — superficie del facilitador. Escritorio denso, navy.
 * Conduce la sesión en vivo: ve la agenda, abre/cierra la actividad y observa
 * los aportes entrando por POLLING (la Cabina SÍ ve autor y grupo). Al cerrar,
 * congela el artefacto (C6).
 */
/** Estilo único de la navegación superior: mismo tamaño, mismo peso, mismo
 *  ícono para TODOS los destinos (antes convivían links text-xs con botones
 *  sm y el ojo no tenía dónde agarrarse). shrink-0 para la fila con scroll. */
const pastillaNav =
  'flex min-h-touch shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 py-1.5 ' +
  'font-sans text-sm font-medium text-content-muted transition-colors duration-fast ' +
  'hover:bg-surface-sunken hover:text-content focus-visible:outline-none focus-visible:shadow-focus ' +
  'disabled:pointer-events-none disabled:opacity-40';

export function CabinaPage() {
  useSurfaceTheme('cabina', 'dark');
  const navigate = useNavigate();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);

  // /cabina?proceso=<id> conduce ESE proceso; sin el parámetro, el hook elige
  // el primero activo como siempre (portada /procesos manda acá con el id).
  const [searchParams, setSearchParams] = useSearchParams();
  const procesoElegido = searchParams.get('proceso');

  // Cambiar de proceso ES una decisión visible (bug real, dos veces: la Cabina
  // resolvía por membresía en silencio y el facilitador miraba la bitácora de
  // OTRA sala mientras el monitor proyectaba la correcta). El selector fija
  // ?proceso= explícito; nunca más una sala adivinada.
  function elegirProceso(id: string) {
    const next = new URLSearchParams(searchParams);
    next.set('proceso', id);
    // Cambiar de proceso invalida el día elegido del proceso anterior.
    next.delete('sesion');
    setSearchParams(next, { replace: true });
  }

  // /cabina?sesion=<id> conduce ESE día. Sin el parámetro se resuelve por la
  // marca «en curso» (que también se pone desde acá). Antes no existía forma
  // de ver los días ni de ir a uno concreto: había que adivinar.
  const sesionElegida = searchParams.get('sesion');

  function elegirJornada(id: string) {
    const next = new URLSearchParams(searchParams);
    next.set('sesion', id);
    setSearchParams(next, { replace: true });
  }

  const { sesionId, procesoActivo, cargando: cargandoSesion } =
    useSesionActiva(procesoElegido, sesionElegida);

  // Borradores en composición: misma queryKey que useSesionActiva, así que
  // comparte caché y no agrega peticiones. Sin esta franja, un borrador recién
  // compuesto queda invisible cuando ya hay un proceso 'activo' en la org.
  const procesos = useQuery({
    queryKey: sesionKeys.procesos,
    queryFn: sesionApi.procesos,
  });
  const borradores = useMemo(
    () => (procesos.data ?? []).filter((p) => p.estado === 'borrador'),
    [procesos.data],
  );

  // Canal en vivo (WS). En vivo empuja; en degradado, el polling toma la posta.
  const sala = useSalaViva(sesionId);
  const pollAgenda = sala.enVivo ? 20_000 : 3000;

  // Agenda de la sesión (poll suave: la estructura cambia poco).
  const sesion = useQuery({
    queryKey: sesionId ? sesionKeys.sesion(sesionId) : ['sesion', 'nada'],
    queryFn: () => sesionApi.sesion(sesionId!),
    enabled: Boolean(sesionId),
    refetchInterval: pollAgenda,
  });

  const actividades = useMemo(
    () => [...(sesion.data?.actividades ?? [])].sort((a, b) => a.orden - b.orden),
    [sesion.data],
  );
  const foco = elegirActividadFoco(actividades);

  // El facilitador puede revisar cualquier actividad de la agenda; por defecto
  // enfoca la "en curso". Si el foco cambia y no había selección, sigue el foco.
  const [seleccionada, setSeleccionada] = useState<string | null>(null);
  // Cambiar de sesión/proceso INVALIDA la selección anterior: sin esto, la
  // actividad de la sesión previa quedaba enfocada y «Aportes en vivo»
  // mostraba aportes de OTRO proceso hasta elegir algo (bug real, 22-ago).
  useEffect(() => {
    setSeleccionada(null);
  }, [sesionId]);
  useEffect(() => {
    if (!seleccionada && foco) setSeleccionada(foco.id);
  }, [foco, seleccionada]);
  // Solo enfoca actividades DE ESTA sesión: mientras la agenda nueva carga,
  // mejor vacío que datos ajenos.
  const deEstaSesion = (id: string | null) =>
    id != null && actividades.some((a) => a.id === id) ? id : null;
  const actividadId = deEstaSesion(seleccionada) ?? foco?.id ?? null;

  // Detalle de la actividad enfocada (XIA + estado + tiempos).
  const actividad = useQuery({
    queryKey: actividadId ? sesionKeys.actividad(actividadId) : ['actividad', 'nada'],
    queryFn: () => sesionApi.actividad(actividadId!),
    enabled: Boolean(actividadId),
    refetchInterval: pollAgenda,
  });

  const estado = actividad.data?.estado;
  const abierta = estado === 'abierta';
  const pausada = estado === 'pausada';
  const enCurso = abierta || pausada;
  const pendientes = useColaPendientes();

  // Aportes en vivo. En vivo llegan por WS; solo polleamos si NO estamos en vivo.
  const aportes = useQuery({
    queryKey: actividadId ? sesionKeys.aportes(actividadId) : ['aportes', 'nada'],
    queryFn: () => sesionApi.aportes(actividadId!),
    enabled: Boolean(actividadId),
    refetchInterval: !sala.enVivo && enCurso ? 2500 : false,
  });

  const tiempo = useTiempoServidor({
    abiertaAt: actividad.data?.abierta_at,
    duracionMin: actividad.data?.duracion_min,
    pausaAcumuladoS: actividad.data?.pausa_acumulado_s,
    estado: actividad.data?.estado ?? 'preparada',
    offsetMs: sala.offsetServidorMs,
  });

  function invalidarActividad() {
    if (actividadId) {
      void qc.invalidateQueries({ queryKey: sesionKeys.actividad(actividadId) });
      void qc.invalidateQueries({ queryKey: sesionKeys.aportes(actividadId) });
    }
    if (sesionId) void qc.invalidateQueries({ queryKey: sesionKeys.sesion(sesionId) });
  }

  const abrirMut = useMutation({
    mutationFn: () => sesionApi.abrir(actividadId!),
    onSuccess: invalidarActividad,
  });

  const cerrarMut = useMutation({
    mutationFn: () => sesionApi.cerrar(actividadId!),
    onSuccess: invalidarActividad,
  });

  const pausarMut = useMutation({
    mutationFn: () => sesionApi.pausar(actividadId!),
    onSuccess: invalidarActividad,
  });

  const reanudarMut = useMutation({
    mutationFn: () => sesionApi.reanudar(actividadId!),
    onSuccess: invalidarActividad,
  });

  const tiempoMut = useMutation({
    mutationFn: (deltaMin: number) => sesionApi.ajustarTiempo(actividadId!, deltaMin),
    onSuccess: invalidarActividad,
  });

  // Moderación (M2): ocultar/restaurar un aporte de la Pantalla. El evento WS
  // 'moderacion' ya parchea la caché; la invalidación es el respaldo.
  const moderarMut = useMutation({
    mutationFn: ({ aporteId, accion }: { aporteId: string; accion: 'ocultar' | 'restaurar' }) =>
      sesionApi.moderar(actividadId!, aporteId, accion),
    onSuccess: () => {
      if (actividadId) {
        void qc.invalidateQueries({ queryKey: sesionKeys.aportes(actividadId) });
      }
    },
  });

  // Diario de campo (M2): marcar/desmarcar una actividad como asíncrona para
  // que reciba aportes entre sesiones mientras su fase siga activa.
  const asincronaMut = useMutation({
    mutationFn: ({ id, activa }: { id: string; activa: boolean }) =>
      sesionApi.marcarAsincrona(id, activa),
    onSuccess: () => {
      if (sesionId) void qc.invalidateQueries({ queryKey: sesionKeys.sesion(sesionId) });
    },
  });

  // El gate fino es del BACKEND (exigir_facilitador, por membresía): aquí solo
  // se decide si mostrar los controles. 'superadmin' faltaba y dejaba al admin
  // mirando una actividad que no podía pausar ni cerrar (bug reportado por el
  // owner); 'miembro' se mantiene porque el rol de membresía por proceso no
  // viaja en /auth/me y el backend rechaza con 403 humano si no corresponde.
  const puedeConducir =
    user?.rol === 'superadmin' ||
    user?.rol === 'facilitador' ||
    user?.rol === 'decisor' ||
    user?.rol === 'miembro';

  async function onLogout() {
    await logout();
    navigate('/login', { replace: true });
  }

  const accionError =
    (abrirMut.error as ApiError | null)?.detail ??
    (cerrarMut.error as ApiError | null)?.detail ??
    (pausarMut.error as ApiError | null)?.detail ??
    (reanudarMut.error as ApiError | null)?.detail ??
    (tiempoMut.error as ApiError | null)?.detail ??
    (moderarMut.error as ApiError | null)?.detail ??
    (asincronaMut.error as ApiError | null)?.detail ??
    null;

  const her = actividad.data?.herramienta;

  return (
    <div className="flex min-h-screen flex-col bg-surface-base">
      {/* Barra superior en tres filas con un rol cada una — antes era una sola
          bolsa flex-wrap donde selects, links y botones caían en filas
          arbitrarias (peor en móvil):
            1. identidad y utilidades (logo · conexión · tema · salir)
            2. contexto de conducción (QUÉ proceso · QUÉ día)
            3. navegación: pastillas uniformes, una línea con scroll en móvil */}
      <header className="border-b border-border">
        <div className="flex items-center gap-2 px-4 pt-2.5 sm:px-6">
          {/* El logo sigue al tema: positivo en claro, negativo en oscuro
              (el negativo era invisible sobre fondo claro). */}
          <img src="/brand/logopositivo.png" alt="ÁGORA" className="h-7 w-auto dark:hidden" />
          <img
            src="/brand/logonegativo.png"
            alt="ÁGORA"
            className="hidden h-7 w-auto dark:block"
          />
          <div className="ml-auto flex items-center gap-1.5">
            {sesionId && (
              <IndicadorConexion estado={sala.estadoConexion} pendientes={pendientes} />
            )}
            <BotonTema />
            <Button
              variant="ghost"
              size="sm"
              onClick={onLogout}
              leadingIcon={<LogOut className="h-4 w-4" aria-hidden="true" />}
            >
              Salir
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap items-start gap-x-6 gap-y-2 px-4 pt-2 sm:px-6">
          {/* QUÉ proceso se conduce: selector SIEMPRE visible (también en
              móvil). El QR del panel y el de la Pantalla salen de la sesión de
              este proceso: si acá dice «agua», los dos QR llevan al agua. */}
          <label className="flex min-h-touch min-w-0 flex-1 items-center gap-2 font-sans text-sm text-content-muted sm:flex-none">
            <span className="shrink-0">Conduciendo</span>
            <select
              value={procesoActivo?.id ?? ''}
              onChange={(e) => elegirProceso(e.target.value)}
              className="min-w-0 flex-1 truncate rounded-md border border-border-strong bg-surface-raised px-2 py-1.5 font-sans text-sm font-semibold text-content focus-visible:outline-none focus-visible:shadow-focus sm:max-w-xs sm:flex-none"
              aria-label="Proceso que estás conduciendo"
            >
              {procesoActivo &&
                !(procesos.data ?? []).some((p) => p.id === procesoActivo.id) && (
                  <option value={procesoActivo.id}>{procesoActivo.nombre}</option>
                )}
              {(procesos.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre}
                  {p.org_nombre ? ` — ${p.org_nombre}` : ''}
                  {p.estado === 'borrador' ? ' (borrador)' : ''}
                </option>
              ))}
            </select>
          </label>
          {/* QUÉ DÍA se conduce: activarlo, cerrarlo o armarlo copiando otro. */}
          {procesoActivo && (
            <SelectorJornada
              procesoId={procesoActivo.id}
              sesionId={sesionId}
              onElegir={elegirJornada}
              puedeConducir={puedeConducir}
            />
          )}
        </div>

        <nav
          aria-label="Secciones de la Cabina"
          className="flex items-center gap-1 overflow-x-auto px-2 py-1.5 sm:px-4"
        >
          <Link
            to="/procesos"
            title="Volver al listado de procesos"
            className={pastillaNav}
          >
            <Home className="h-4 w-4" aria-hidden="true" />
            Inicio
          </Link>
          {procesoActivo && (
            <Link
              to={`/compositor/${procesoActivo.id}`}
              title="Abrir este proceso en el compositor (se puede seguir editando lo que no ha corrido)"
              className={pastillaNav}
            >
              <PenLine className="h-4 w-4" aria-hidden="true" />
              Componer
            </Link>
          )}
          {procesoActivo && (
            <Link
              to={`/participantes/${procesoActivo.id}`}
              title="Gestionar participantes, roles y grupos de este proceso"
              className={pastillaNav}
            >
              <Users className="h-4 w-4" aria-hidden="true" />
              Participantes
            </Link>
          )}
          {/* El facilitador no encontraba cómo crear un proyecto nuevo desde
              la Cabina: acceso directo al compositor con proceso en blanco.
              Los participantes (miembro) no lo ven: aportan, no componen. */}
          {(user?.rol === 'facilitador' || user?.rol === 'admin_org' || user?.rol === 'superadmin') && (
            <button type="button" onClick={() => navigate('/nuevo')} className={pastillaNav}>
              <Plus className="h-4 w-4" aria-hidden="true" />
              Nuevo proceso
            </button>
          )}
          {(user?.rol === 'admin_org' || user?.rol === 'superadmin') && (
            <Link to="/organizacion" title="Mi organización: identidad y usuarios" className={pastillaNav}>
              <Building2 className="h-4 w-4" aria-hidden="true" />
              Organización
            </Link>
          )}
          <Link to="/cuenta" title="Mi cuenta: perfil, plan y consumo" className={pastillaNav}>
            <UserRound className="h-4 w-4" aria-hidden="true" />
            Mi cuenta
          </Link>
          {/* Gestor de usuarios y planes: solo el superadmin lo ve. */}
          {user?.rol === 'superadmin' && (
            <Link to="/admin" title="Gestor de usuarios y planes" className={pastillaNav}>
              <ShieldCheck className="h-4 w-4" aria-hidden="true" />
              Usuarios
            </Link>
          )}
          <span className="mx-1 h-5 w-px shrink-0 bg-border" aria-hidden="true" />
          <button
            type="button"
            onClick={() => procesoActivo && navigate(`/propuesta/${procesoActivo.id}`)}
            disabled={!procesoActivo}
            className={pastillaNav}
          >
            <FileText className="h-4 w-4" aria-hidden="true" />
            Propuesta
          </button>
          <button
            type="button"
            onClick={() => procesoActivo && navigate(`/tablero/${procesoActivo.id}`)}
            disabled={!procesoActivo}
            className={pastillaNav}
          >
            <BarChart3 className="h-4 w-4" aria-hidden="true" />
            Tablero
          </button>
          <span className="flex-1" aria-hidden="true" />
          <Button
            variant="secondary"
            size="sm"
            onClick={() => sesionId && navigate(`/pantalla/${sesionId}`)}
            disabled={!sesionId}
            leadingIcon={<MonitorPlay className="h-4 w-4" aria-hidden="true" />}
            className="shrink-0"
          >
            Abrir Pantalla
          </Button>
        </nav>
      </header>

      {/* Cola de audio: el micrófono de sala graba desde ACÁ, así que su red de
          seguridad (descargar / reintentar lo que no entró al acta) tiene que
          estar a la vista en la Cabina, no solo en el Bolsillo. */}
      <ColaAudio />

      {/* Procesos en composición: acceso discreto a los borradores para que no
          queden invisibles cuando ya hay un proceso activo elegido arriba. */}
      {borradores.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-border bg-surface-raised px-4 py-1.5 sm:px-6">
          <span className="font-sans text-xs font-semibold uppercase tracking-wide text-content-muted">
            Procesos en composición
          </span>
          {borradores.map((p) => (
            <Link
              key={p.id}
              to={`/compositor/${p.id}`}
              title={`Seguir componiendo «${p.nombre}»`}
              className="flex min-h-touch items-center gap-1.5 rounded-md px-2 font-sans text-sm text-content transition-colors duration-fast hover:bg-surface-sunken focus-visible:outline-none focus-visible:shadow-focus"
            >
              <PenLine className="h-3.5 w-3.5 shrink-0 text-content-muted" aria-hidden="true" />
              <span className="max-w-[16rem] truncate">
                {p.nombre}
                {p.org_nombre ? ` · ${p.org_nombre}` : ''}
              </span>
              <Badge variant="neutral">Borrador</Badge>
            </Link>
          ))}
        </div>
      )}

      {/* XIA de la herramienta activa + tiempo restante */}
      {her && (
        <div className="px-4 pt-4 sm:px-6">
          <XiaBar
            que={her.xia.que}
            porQue={her.xia.por_que}
            hacia={her.xia.hacia}
            tiempoRestante={tiempo.texto}
          />
        </div>
      )}

      {/* Cuerpo. ≥1024px: itinerario a la izquierda (stepper vertical) + foco.
          <1024px: el itinerario se vuelve barra horizontal arriba y el foco
          ocupa todo el ancho. */}
      <div className="flex flex-1 flex-col gap-4 px-4 py-4 sm:px-6 sm:py-6 lg:grid lg:grid-cols-[20rem_1fr] lg:items-start lg:gap-6">
        {/* DÓNDE ESTÁ LA SESIÓN: el hilo del método */}
        <aside className="flex flex-col gap-4">
          <Itinerario
            actividades={actividades}
            actividadId={actividadId}
            cargando={cargandoSesion || sesion.isLoading}
            puedeConducir={puedeConducir}
            marcandoId={asincronaMut.isPending ? asincronaMut.variables.id : null}
            onSelect={setSeleccionada}
            onToggleDiario={(item) =>
              asincronaMut.mutate({ id: item.id, activa: !item.asincrona })
            }
          />

          {/* Panel secundario: colapsado y discreto, debajo del itinerario. */}
          {sesionId && (
            <InvitarSala sesionId={sesionId} procesoId={procesoActivo?.id ?? null} />
          )}

          {/* Validación de documentos: links directos asíncronos, sin conducir. */}
          {procesoActivo && puedeConducir && (
            <PanelValidaciones procesoId={procesoActivo.id} />
          )}
        </aside>

        {/* Área principal */}
        <main className="flex flex-col gap-4">
          {!sesionId && !cargandoSesion ? (
            <SinSesion />
          ) : !actividad.data ? (
            <Card title="Preparando la sesión">
              <p className="font-sans text-sm text-content-muted">
                Elegí una actividad de la agenda para conducirla.
              </p>
            </Card>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h1 className="flex items-center gap-2 font-display text-2xl font-semibold text-content">
                  <LayoutDashboard className="h-6 w-6 text-primary" aria-hidden="true" />
                  {actividad.data.titulo ?? her?.nombre}
                </h1>
                <EstadoBadge estado={actividad.data.estado} />
              </div>

              {/* Nota operativa del guion (config.nota_cabina): instrucción
                  SOLO para quien conduce, tal cual la escribió el compositor. */}
              {actividad.data.nota_cabina && (
                <div className="flex items-start gap-2 rounded-md border border-info bg-surface-raised px-4 py-3">
                  <Info className="mt-0.5 h-4 w-4 shrink-0 text-info" aria-hidden="true" />
                  <div className="min-w-0">
                    <p className="font-sans text-xs font-semibold uppercase tracking-wide text-info">
                      Nota operativa
                    </p>
                    <p className="mt-0.5 whitespace-pre-line font-sans text-sm text-content">
                      {actividad.data.nota_cabina}
                    </p>
                  </div>
                </div>
              )}

              {/* QUÉ HACER AHORA: una acción principal por estado */}
              <AccionPrincipal
                estado={actividad.data.estado}
                puedeConducir={puedeConducir}
                totalAportes={actividad.data.total_aportes}
                error={accionError}
                abriendo={abrirMut.isPending}
                cerrando={cerrarMut.isPending}
                pausando={pausarMut.isPending}
                reanudando={reanudarMut.isPending}
                sumandoTiempo={tiempoMut.isPending}
                onAbrir={() => abrirMut.mutate()}
                onCerrar={() => cerrarMut.mutate()}
                onPausar={() => pausarMut.mutate()}
                onReanudar={() => reanudarMut.mutate()}
                onSumarMinuto={() => tiempoMut.mutate(1)}
                onRestarMinuto={() => tiempoMut.mutate(-1)}
              />

              {/* Paneles de conducción (ADR-0005 §6): ronda por secciones,
                  supervoto del decisor y lo que el manifiesto declare — el
                  registro resuelve el componente, la página no ramifica. */}
              <PanelConduccion actividad={actividad.data} puedeConducir={puedeConducir} />

              {/* Presencia agregada por grupo (C7: nunca por persona) */}
              {sala.presencia && sala.presencia.conectados_total > 0 && (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md border border-border bg-surface-raised px-4 py-2.5">
                  <span className="flex items-center gap-2 font-sans text-sm font-semibold text-content">
                    <Users className="h-4 w-4 text-cyan" aria-hidden="true" />
                    {sala.presencia.conectados_total} conectados
                  </span>
                  {sala.presencia.por_grupo.map((g) => (
                    <span key={g.grupo} className="font-sans text-sm text-content-muted">
                      {g.grupo}:{' '}
                      <span className="font-semibold tabular-nums text-content">{g.n}</span>
                    </span>
                  ))}
                </div>
              )}

              {/* Aportes en vivo (la Cabina ve autor + grupo) */}
              <Card
                title="Aportes en vivo"
                footer={
                  <span className="flex items-center gap-2">
                    <Users className="h-4 w-4" aria-hidden="true" />
                    {aportes.data?.agregado.total ?? actividad.data.total_aportes ?? 0} aportes
                    {aportes.data?.agregado.por_grupo?.length
                      ? ` · ${aportes.data.agregado.por_grupo
                          .map((g) => `${g.grupo}: ${g.n}`)
                          .join('  ·  ')}`
                      : ''}
                  </span>
                }
              >
                <ListaAportes
                  cargando={aportes.isLoading}
                  aportes={aportes.data?.aportes ?? []}
                  estado={actividad.data.estado}
                  puedeModerar={puedeConducir}
                  moderandoId={moderarMut.isPending ? moderarMut.variables.aporteId : null}
                  onModerar={(aporteId, accion) => moderarMut.mutate({ aporteId, accion })}
                />
              </Card>
            </>
          )}
        </main>
      </div>
    </div>
  );
}

/* ------------------------------- Piezas -------------------------------- */

/**
 * Panel colapsable «Invitar a la sala» (Lote 6): muestra el código de la
 * sesión (para copiarlo) y el PIN COMPARTIDO del proceso — dato público de
 * proyección, se muestra también en la Pantalla —, o el formulario para
 * fijarlo si aún no existe. Con esto el participante entra por «Acceso de
 * sala» escribiendo su nombre, sin cuenta previa.
 */
function InvitarSala({
  sesionId,
  procesoId,
}: {
  sesionId: string;
  /** Proceso dueño del PIN compartido; null si aún no se resolvió. */
  procesoId: string | null;
}) {
  const qc = useQueryClient();
  // Colapsado por defecto: es un panel secundario; el itinerario manda.
  const [abierto, setAbierto] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const [editando, setEditando] = useState(false);
  const [pinNuevo, setPinNuevo] = useState('');

  const sala = useQuery({
    queryKey: ['sala', sesionId],
    queryFn: () => sesionApi.sala(sesionId),
  });

  const pinMut = useMutation({
    mutationFn: (pin: string) => sesionApi.fijarPinSala(procesoId!, pin),
    onSuccess: () => {
      setEditando(false);
      setPinNuevo('');
      void qc.invalidateQueries({ queryKey: ['sala', sesionId] });
    },
  });

  // Mismo patrón que accionError del cuerpo: detail humano de la API en rojo.
  const accionError = (pinMut.error as ApiError | null)?.detail ?? null;

  const pin = sala.data?.pin ?? null;
  const pinValido = /^\d{4}$/.test(pinNuevo);
  const mostrarFormulario = !pin || editando;

  async function copiarCodigo() {
    if (!sala.data) return;
    try {
      await navigator.clipboard.writeText(sala.data.codigo_sesion);
      setCopiado(true);
      window.setTimeout(() => setCopiado(false), 2000);
    } catch {
      // Sin permiso de portapapeles: el código queda visible para copiarlo a mano.
    }
  }

  return (
    <section className="rounded-md border border-border bg-surface-raised">
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        aria-expanded={abierto}
        className="flex min-h-touch w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-left focus-visible:outline-none focus-visible:shadow-focus"
      >
        <span className="flex items-center gap-2 font-display text-sm font-semibold uppercase tracking-wide text-content-muted">
          <UsersRound className="h-4 w-4" aria-hidden="true" />
          Invitar a la sala
        </span>
        {abierto ? (
          <ChevronUp className="h-4 w-4 shrink-0 text-content-muted" aria-hidden="true" />
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0 text-content-muted" aria-hidden="true" />
        )}
      </button>

      {abierto && (
        <div className="despliega flex flex-col gap-3 border-t border-border px-3 py-3">
          {sala.isLoading ? (
            <EsqueletoLineas lineas={3} />
          ) : !sala.data ? (
            <p className="flex items-center gap-1.5 font-sans text-sm text-content-muted">
              <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
              No se pudieron cargar los datos de la sala.
            </p>
          ) : (
            <>
              {/* Código de la sesión activa */}
              <div>
                <p className="font-sans text-xs uppercase tracking-wide text-content-muted">
                  Código de la sesión
                </p>
                <div className="mt-1 flex items-center gap-1">
                  <code className="min-w-0 flex-1 break-all font-mono text-sm tabular-nums text-content">
                    {sala.data.codigo_sesion}
                  </code>
                  <button
                    type="button"
                    onClick={() => void copiarCodigo()}
                    aria-label="Copiar el código de la sesión"
                    title="Copiar el código de la sesión"
                    className="flex min-h-touch min-w-touch shrink-0 items-center justify-center gap-1 rounded-md px-1 font-sans text-xs text-content-muted transition-colors duration-fast hover:bg-surface-sunken hover:text-content focus-visible:outline-none focus-visible:shadow-focus"
                  >
                    {copiado ? (
                      <>
                        <Check className="h-4 w-4 text-success" aria-hidden="true" />
                        <span className="text-success">Copiado</span>
                      </>
                    ) : (
                      <Copy className="h-4 w-4" aria-hidden="true" />
                    )}
                  </button>
                </div>
              </div>

              {/* PIN compartido del proceso */}
              <div>
                <p className="font-sans text-xs uppercase tracking-wide text-content-muted">
                  PIN de la sala
                </p>
                {pin && !editando && (
                  <div className="mt-1 flex items-center gap-2">
                    <span className="font-display text-3xl font-semibold tabular-nums tracking-widest text-content">
                      {pin}
                    </span>
                    <button
                      type="button"
                      onClick={() => setEditando(true)}
                      className="flex min-h-touch items-center rounded-md px-2 font-sans text-xs text-content-muted transition-colors duration-fast hover:bg-surface-sunken hover:text-content focus-visible:outline-none focus-visible:shadow-focus"
                    >
                      Cambiar
                    </button>
                  </div>
                )}
                {mostrarFormulario && (
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={4}
                      value={pinNuevo}
                      onChange={(e) => setPinNuevo(e.target.value.replace(/\D/g, ''))}
                      placeholder="4 dígitos"
                      aria-label="PIN de la sala (4 dígitos)"
                      className="min-h-touch w-28 rounded-md border border-border-strong bg-surface-sunken px-3 text-center font-sans text-base tabular-nums tracking-widest text-content placeholder:text-content-muted focus-visible:outline-none focus-visible:shadow-focus"
                    />
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={!pinValido || !procesoId}
                      loading={pinMut.isPending}
                      onClick={() => pinMut.mutate(pinNuevo)}
                    >
                      Fijar PIN
                    </Button>
                    {pin && (
                      <button
                        type="button"
                        onClick={() => {
                          setEditando(false);
                          setPinNuevo('');
                        }}
                        className="flex min-h-touch items-center rounded-md px-2 font-sans text-xs text-content-muted transition-colors duration-fast hover:bg-surface-sunken hover:text-content focus-visible:outline-none focus-visible:shadow-focus"
                      >
                        Cancelar
                      </button>
                    )}
                  </div>
                )}
                {accionError && (
                  <p className="mt-1.5 flex items-center gap-1.5 font-sans text-sm text-error">
                    <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
                    {accionError}
                  </p>
                )}
              </div>

              {/* QR de entrada: SIEMPRE (pin opcional del componente). Con PIN
                  la URL lo lleva; sin PIN el QR lleva solo ?sala= y la persona
                  entra con Google o con su PIN personal. */}
              <div className="flex items-center gap-3">
                <div className="shrink-0 rounded-md bg-white p-2">
                  <QrSala
                    codigoSesion={sala.data.codigo_sesion}
                    pin={pin}
                    tamano={140}
                  />
                </div>
                <p className="font-sans text-xs text-content-muted">
                  {pin
                    ? 'El mismo QR aparece grande en la Pantalla.'
                    : 'Sin PIN fijado, quien escanea entra con su cuenta de Google o con su PIN personal.'}
                </p>
              </div>

              <p className="font-sans text-xs text-content-muted">
                {pin
                  ? 'Los participantes entran en agora.inteligencia.com.gt → «Acceso de sala» con este código y PIN, y escriben su nombre.'
                  : 'Los participantes también pueden entrar en agora.inteligencia.com.gt → «Acceso de sala» con este código y su PIN personal.'}
              </p>
            </>
          )}
        </div>
      )}
    </section>
  );
}

function EstadoBadge({ estado }: { estado: ActividadEstado }) {
  if (estado === 'abierta') return <Badge variant="primary">En vivo</Badge>;
  if (estado === 'pausada') return <Badge variant="warning">En pausa</Badge>;
  if (estado === 'cerrada') return <Badge variant="success">Acta congelada</Badge>;
  return <Badge variant="neutral">Preparada</Badge>;
}

function ListaAportes({
  cargando,
  aportes,
  estado,
  puedeModerar,
  moderandoId,
  onModerar,
}: {
  cargando: boolean;
  aportes: Aporte[];
  estado: ActividadEstado;
  puedeModerar: boolean;
  /** Id del aporte cuya moderación está en vuelo (spinner en su botón). */
  moderandoId: string | null;
  onModerar: (aporteId: string, accion: 'ocultar' | 'restaurar') => void;
}) {
  if (aportes.length === 0) {
    if (cargando) {
      return <EsqueletoAportes filas={3} />;
    }
    // Estado vacío que enseña, con ejemplo fantasma GT.
    return (
      <div>
        <p className="flex items-center gap-2 font-sans text-sm text-content-muted">
          <Inbox className="h-4 w-4" aria-hidden="true" />
          {estado === 'preparada'
            ? 'Abrí la actividad para que la sala empiece a aportar desde su teléfono.'
            : estado === 'cerrada'
              ? 'La actividad cerró sin aportes registrados.'
              : 'Todavía no llegan aportes. Aparecerán acá con su autor y grupo, en vivo.'}
        </p>
        <div className="mt-3 rounded-md border border-dashed border-border px-3 py-2 opacity-70">
          <p className="font-sans text-xs uppercase tracking-wide text-content-muted">
            Así se verá cada aporte
          </p>
          <p className="mt-1 font-sans text-sm text-content">
            «En la zona 3 el agua llega solo tres días por semana.»
          </p>
          <p className="mt-1 flex items-center gap-2 font-sans text-xs text-content-muted">
            <UserRound className="h-3.5 w-3.5" aria-hidden="true" />
            Rosa Chávez · Grupo Norte
          </p>
        </div>
      </div>
    );
  }

  // Lo más reciente ARRIBA (pedido del owner): el facilitador mira lo que
  // acaba de llegar, no el arranque de la actividad. El backend sirve el hilo
  // cronológico ASC (contrato del polling ?desde= y del snapshot C6): esto es
  // solo orden de PRESENTACIÓN.
  const recientesPrimero = [...aportes].reverse();

  return (
    <ul className="flex flex-col divide-y divide-border">
      {recientesPrimero.map((a) => (
        <li key={a.id} className="flex items-start gap-2 py-2.5 first:pt-0 last:pb-0">
          {/* El texto se atenúa cuando está oculto, pero JAMÁS desaparece de la
              Cabina (C1): el facilitador debe poder releer lo que ocultó. */}
          <div className={`flex min-w-0 flex-1 flex-col gap-1.5 ${a.oculto ? 'opacity-60' : ''}`}>
            {esMedia(a.tipo) ? (
              <MediaAporte aporte={a} />
            ) : (
              <p className="font-sans text-sm text-content">{resumirAporte(a)}</p>
            )}
            <p className="flex flex-wrap items-center gap-2 font-sans text-xs text-content-muted">
              <span className="flex items-center gap-1">
                <UserRound className="h-3.5 w-3.5" aria-hidden="true" />
                {a.user_nombre ?? 'Anónimo'}
              </span>
              {(a.payload ?? {}).supervoto === true && (
                <Badge variant="primary">Supervoto</Badge>
              )}
              {a.grupo_nombre && (
                <Badge variant="neutral">{a.grupo_nombre}</Badge>
              )}
              <span className="tabular-nums">
                {new Date(a.created_at).toLocaleTimeString('es-GT', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
              {a.oculto && <Badge variant="warning">Oculto de la Pantalla</Badge>}
            </p>
          </div>

          {puedeModerar && (
            <BotonModerar
              oculto={Boolean(a.oculto)}
              moderando={moderandoId === a.id}
              deshabilitado={moderandoId !== null}
              onClick={() => onModerar(a.id, a.oculto ? 'restaurar' : 'ocultar')}
            />
          )}
        </li>
      ))}
    </ul>
  );
}

/**
 * Botón discreto de moderación por aporte: ocultar de la Pantalla (EyeOff) o
 * restaurar (Eye). Target táctil >=44px aunque la Cabina sea densa.
 */
function BotonModerar({
  oculto,
  moderando,
  deshabilitado,
  onClick,
}: {
  oculto: boolean;
  moderando: boolean;
  deshabilitado: boolean;
  onClick: () => void;
}) {
  const etiqueta = oculto ? 'Restaurar aporte en la Pantalla' : 'Ocultar aporte de la Pantalla';
  const Icono = oculto ? Eye : EyeOff;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={deshabilitado}
      aria-label={etiqueta}
      title={etiqueta}
      className="flex min-h-touch min-w-touch shrink-0 items-center justify-center rounded-md text-content-muted transition-colors duration-fast hover:bg-surface-sunken hover:text-content focus-visible:outline-none focus-visible:shadow-focus disabled:pointer-events-none disabled:opacity-50"
    >
      {moderando ? (
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      ) : (
        <Icono className="h-4 w-4" aria-hidden="true" />
      )}
    </button>
  );
}

/* -------------------------- Evidencia (M2) ----------------------------- */

/**
 * Renderiza un aporte de EVIDENCIA en la Cabina: reproductor para audio,
 * miniatura para foto, enlace de descarga para archivo; y debajo, cuando es
 * audio, la TRANSCRIPCIÓN citable con su ⓘ (que apunta al aporte). Mientras el
 * worker no devuelve el texto, marca "transcribiendo…".
 */
function MediaAporte({ aporte }: { aporte: Aporte }) {
  const payload = (aporte.payload ?? {}) as MediaPayload;
  const filename = payload.filename ?? 'evidencia';
  const media = useMediaUrl(aporte.id);
  // Safari (Mac/iPhone) NO reproduce audio/webm (Opus), el formato con que
  // graba Chrome/Android: si el <audio> falla, ofrecemos la descarga en vez
  // de un reproductor mudo.
  const [fallaAudio, setFallaAudio] = useState(false);

  return (
    <div className="flex flex-col gap-2">
      {aporte.tipo === 'audio' && (
        <div className="flex flex-wrap items-center gap-2">
          <Mic className="h-4 w-4 shrink-0 text-violet" aria-hidden="true" />
          {media.cargando ? (
            <CargandoMedia texto="Cargando audio…" />
          ) : media.url && !fallaAudio ? (
            <audio
              controls
              src={media.url}
              className="h-9 w-full max-w-md"
              onError={() => setFallaAudio(true)}
            >
              Tu navegador no puede reproducir este audio.
            </audio>
          ) : media.url && fallaAudio ? (
            <span className="font-sans text-sm text-content-muted">
              Tu navegador no reproduce este formato ({filename.split('.').pop()}).
            </span>
          ) : (
            <ErrorMedia texto="No se pudo cargar el audio." />
          )}
          {media.url && (
            <a
              href={media.url}
              download={filename}
              className="flex min-h-touch items-center gap-1 rounded-md px-2 font-sans text-xs text-content-muted transition-colors duration-fast hover:bg-surface-sunken hover:text-content focus-visible:outline-none focus-visible:shadow-focus"
              title={`Descargar ${filename}`}
            >
              <Download className="h-3.5 w-3.5" aria-hidden="true" />
              Descargar
            </a>
          )}
        </div>
      )}

      {aporte.tipo === 'foto' && (
        <div>
          {media.cargando ? (
            <CargandoMedia texto="Cargando foto…" />
          ) : media.url ? (
            <a href={media.url} target="_blank" rel="noreferrer" className="inline-block">
              <img
                src={media.url}
                alt={`Foto adjunta: ${filename}`}
                className="max-h-40 rounded-md border border-border object-cover"
              />
            </a>
          ) : (
            <ErrorMedia texto="No se pudo cargar la foto." icono={ImageOff} />
          )}
        </div>
      )}

      {aporte.tipo === 'archivo' && (
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 shrink-0 text-content-muted" aria-hidden="true" />
          {media.cargando ? (
            <CargandoMedia texto="Preparando archivo…" />
          ) : media.url ? (
            <a
              href={media.url}
              download={filename}
              className="inline-flex min-h-touch items-center gap-1.5 font-sans text-sm font-semibold text-primary hover:underline"
            >
              <Download className="h-4 w-4" aria-hidden="true" />
              {filename}
            </a>
          ) : (
            <ErrorMedia texto="No se pudo cargar el archivo." />
          )}
        </div>
      )}

      {/* Transcripción citable del audio (violeta = IA). Su ⓘ apunta al aporte. */}
      {aporte.tipo === 'audio' && <Transcripcion aporte={aporte} />}
    </div>
  );
}

function Transcripcion({ aporte }: { aporte: Aporte }) {
  const trans = aporte.transcripcion;
  if (trans) {
    return (
      <div className="rounded-md border border-violet px-3 py-2">
        <p className="flex items-start gap-2 font-sans text-sm text-content">
          <span title={`Cita: transcripción del aporte ${aporte.id}`} className="mt-0.5 shrink-0">
            <Info className="h-4 w-4 text-violet" aria-hidden="true" />
            <span className="sr-only">Transcripción citable — apunta al aporte</span>
          </span>
          <span>{trans.texto}</span>
        </p>
      </div>
    );
  }
  return (
    <p className="flex items-center gap-2 font-sans text-sm text-violet">
      <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden="true" />
      Transcribiendo…
    </p>
  );
}

function CargandoMedia({ texto }: { texto: string }) {
  return (
    <span className="flex items-center gap-2 font-sans text-sm text-content-muted">
      <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden="true" />
      {texto}
    </span>
  );
}

function ErrorMedia({ texto, icono: Icono = AlertCircle }: { texto: string; icono?: LucideIcon }) {
  return (
    <span className="flex items-center gap-2 font-sans text-sm text-content-muted">
      <Icono className="h-4 w-4 shrink-0" aria-hidden="true" />
      {texto}
    </span>
  );
}

function SinSesion() {
  return (
    <Card title="No hay una sesión en curso">
      <p className="font-sans text-sm text-content-muted">
        Cuando tu proceso tenga una sesión activa, acá vas a conducir la agenda:
        abrir cada actividad, ver los aportes de la sala en vivo y cerrar el acta
        para dejar el artefacto congelado y trazable.
      </p>
      <p className="mt-3 font-sans text-sm text-content-muted">
        Ejemplo: «Lluvia de ideas sobre agua potable» → los vecinos aportan desde
        su teléfono → al cerrar, cada idea queda citada en la propuesta de proyecto.
      </p>
    </Card>
  );
}
